import { LoadingSpinner, TableCellId } from "@bluefalcon/components-package";
import { User, UserCreateRequest, UserUpdateRequest } from "@bluefalcon/users-management-client-fetch";
import { Box, Button, Flex, Input, Table, TableCaption, Tbody, Td, Th, Thead, Tr, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { authService, usersService } from "../../services";
import { Link } from "react-router-dom";
import { AddEditUserModal } from "../../modals/add-edit-user.modal";

type UsersTableProps = {
    tenantId?: string;
    showAddButton: boolean;
};

export const UsersTable = ({
    tenantId,
    showAddButton,
}: UsersTableProps) => {

    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [isLoadingResetPassword, setIsLoadingResetPassword] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedUser, setSelectedUser] = useState<User>();
    const [query, setQuery] = useState('');

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const { results, total: _total } = await usersService.searchUsers({
                query,
                tenantId,
            });
            setUsers(results);
            setTotal(_total);
        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    };

    const onAddButtonClick = () => {
        setSelectedUser(undefined);
        onOpen();
    };

    const onCreateUser = async (req: UserCreateRequest) => {
        try {
            setIsModalLoading(true);
            await usersService.createUser({
                ...req,
                tenantId,
            });
            fetchUsers();
            onClose();
        } catch (error) {

        } finally {
            setIsModalLoading(false);
        }
    };

    const onResetPasswordButtonClick = async (userId: string) => {
        try {
            setIsLoadingResetPassword(true);
            await authService.sendResetPasswordEmail(userId);
        } catch (error) {

        } finally {
            setIsLoadingResetPassword(false);
        }
    };

    const onUpdateUser = async (id: string, req: UserUpdateRequest) => {
        try {
            setIsModalLoading(true);
            await usersService.updateUserById(id, req);
            fetchUsers();
            onClose();
        } catch (error) {

        } finally {
            setIsModalLoading(false);
        }
    };

    const search = async () => {
        await fetchUsers();
    }

    useEffect(() => {
        fetchUsers();
    }, []);

    if (isLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            <Flex mb={2}>
                <form onSubmit={search}>
                    <Flex>
                        <Input onChange={(e) => setQuery(e.target.value)} value={query} placeholder="search..." type={'search'} />
                        <Button type="submit" onClick={search} ml={2} colorScheme="primary">Search</Button>
                    </Flex>
                </form>
                {showAddButton && <Button onClick={onAddButtonClick} ml={'auto'} colorScheme="teal">Add User</Button>}
            </Flex>
            {users.length ? <Table variant="simple">
                <TableCaption textAlign={'left'}>Total Users: {total}</TableCaption>
                <Thead>
                    <Tr>
                        <Th>ID</Th>
                        <Th>First Name</Th>
                        <Th>Last Name</Th>
                        <Th>Email</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {users.map((user) => (
                        <Tr key={user.id}>
                            <Td><TableCellId label={user.id} route={`/users/${user.id}`} /></Td>
                            <Td>{user.firstName}</Td>
                            <Td>{user.lastName}</Td>
                            <Td>{user.email}</Td>
                            <Td>
                                <Flex>
                                    <Button as={Link} to={`/users/${user.id}`} mr={2} size="sm">
                                        Details
                                    </Button>
                                    {tenantId && <Button mr={2} colorScheme="red" size="sm">
                                        Revoke
                                    </Button>}
                                    <Button isLoading={isLoadingResetPassword} onClick={() => onResetPasswordButtonClick(user.id)} colorScheme="primary" size="sm">
                                        Reset Password
                                    </Button>
                                </Flex>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table> : <Box textAlign={'center'} mt={5}>
                No users found.
            </Box>}
            <AddEditUserModal
                closeModal={onClose}
                modalState={isOpen}
                user={selectedUser}
                handleAddUser={onCreateUser}
                handleEditUser={onUpdateUser}
                isLoading={isModalLoading}
            />
        </>
    )
};
